import { PUBLIC_API_BASE, PUBLIC_MEDIA_BASE } from '@/lib/public-config';
import type { Media } from '@/lib/dto';

/**
 * Strip a redundant leading `/media/` path segment from a media-host URL.
 *
 * This tenant's media CDN (`media.<host>`) serves every file from the root
 * (`https://media.<host>/file.webp`) — that is the form every entity media URL
 * arrives in. The SEO Engine, however, sometimes emits `og.image` with a stale
 * `/media/` prefix (`https://media.<host>/media/file.webp`), which 404s. Any
 * absolute URL whose hostname starts with `media.` and whose path begins with
 * `/media/` is that bug, so we collapse it here.
 */
export function stripRedundantMediaSegment(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname.startsWith('media.') && u.pathname.startsWith('/media/')) {
      u.pathname = u.pathname.slice('/media'.length);
      return u.toString();
    }
  } catch {
    // Not a parseable absolute URL — leave it untouched.
  }
  return url;
}

/** Legacy media hosts for this deployment. The site host comes from
 *  `NEXT_PUBLIC_TENANT_HOST` when set (lets a local base like
 *  http://localhost:3000/media still recognise the real hosts), else from the
 *  canonical media base (`www.` dropped). Both are build-time inlined, so
 *  server and client agree:
 *  - `media.<site>/<file>` — the media CDN subdomain
 *  - `api.<site>/media/<file>` — the API host, which serves the same files */
function legacyMediaFile(u: URL, base: string): string | null {
  let site = (process.env.NEXT_PUBLIC_TENANT_HOST ?? '').trim();
  if (!site) {
    try {
      site = new URL(base).hostname;
    } catch {
      return null;
    }
  }
  site = site.replace(/^www\./, '');
  if (u.hostname === `media.${site}`) return u.pathname;
  if (u.hostname === `api.${site}` && u.pathname.startsWith('/media/')) {
    return u.pathname.slice('/media'.length);
  }
  return null;
}

/**
 * Move a media URL onto the canonical first-party base
 * (`NEXT_PUBLIC_MEDIA_BASE`, e.g. `https://<site>/media`), so pages, OG,
 * JSON-LD and sitemaps reference the final URL directly instead of relying on
 * the `media.<host>` → `/media` 301. Only this deployment's own media hosts
 * (`media.<site>`, `api.<site>/media/`) are rewritten — third-party hosts pass
 * through. With no media base configured this is just
 * `stripRedundantMediaSegment`.
 */
export function toCanonicalMediaUrl(url: string, base = PUBLIC_MEDIA_BASE): string {
  const stripped = stripRedundantMediaSegment(url);
  if (!base) return stripped;
  try {
    const u = new URL(stripped);
    const file = legacyMediaFile(u, base);
    if (!file) return stripped;
    return `${base.replace(/\/+$/, '')}${file}${u.search}`;
  } catch {
    return stripped;
  }
}

/**
 * Resolves an API-returned media URL to a publicly-loadable URL.
 * - Absolute URLs are canonicalised (see `toCanonicalMediaUrl`).
 * - Relative `/media/<file>` paths go on the canonical media base when one is
 *   configured; any other relative path is prefixed with the public API base.
 */
export function resolveMediaUrl(input: string | null | undefined): string | null {
  if (!input) return null;
  if (/^https?:\/\//i.test(input)) return toCanonicalMediaUrl(input);
  if (PUBLIC_MEDIA_BASE) {
    const file = input.replace(/^\/+/, '').replace(/^media\//, '');
    if (input.startsWith('/media/') || !input.includes('/')) {
      return `${PUBLIC_MEDIA_BASE}/${file}`;
    }
  }
  const base = PUBLIC_API_BASE.replace(/\/+$/, '');
  if (input.startsWith('/')) return `${base}${input}`;
  return `${base}/${input}`;
}

export function media(media: Media): { src: string; alt: string; caption: string | null } | null {
  if (!media || !media.url) return null;
  const src = resolveMediaUrl(media.url);
  if (!src) return null;
  return { src, alt: media.alt ?? '', caption: media.caption ?? null };
}
